import {
  CharacterBaseStats,
  StatRangeData,
  StatRanges,
  UpgradeComparisonData,
} from "types";

import Tooltip from "components/base/Tooltip";
import { getStatRangeData } from "libs/stats";
import {
  DerivedStatName,
  StatDiffIcon,
  StatIcon,
  StatIconWrapper,
  StatRangeWrapper,
  StatValue,
  StatValueDiff,
  ValueWrapper,
} from "./DerivedStatsBase";
import TooltipDerivedStatRange from "./TooltipDerivedStatRange";
import { getComparisonIndicator } from "libs/item";

interface StatRangeProps {
  slug: keyof StatRanges;
  statRange: StatRangeData;
  baseRange: StatRangeData;
  upgradesRange: StatRangeData;
  highlightStat: keyof CharacterBaseStats | null;
  previewDiff?: UpgradeComparisonData | null;
}

export function StatRange({
  slug,
  statRange,
  baseRange,
  upgradesRange,
  highlightStat = null,
  previewDiff = null,
}: StatRangeProps) {
  const statRangeSlug = slug as keyof StatRanges;
  const statRangeInfo = getStatRangeData(statRangeSlug);

  const minDiffValue = previewDiff?.[statRange.minStat]?.difference || 0;
  const maxDiffValue = previewDiff?.[statRange.maxStat]?.difference || 0;

  const isStatFocusActive = !!highlightStat || !!previewDiff;
  const isHighlightedStat =
    !!highlightStat && statRangeInfo.baseStats.includes(highlightStat);
  const isPreviewDiffMinStat =
    !!previewDiff?.[statRange.minStat] && minDiffValue;
  const isPreviewDiffMaxStat =
    !!previewDiff?.[statRange.maxStat] && maxDiffValue;
  const isPreviewDiffStat = isPreviewDiffMinStat || isPreviewDiffMaxStat;

  const isDim = isStatFocusActive && !isHighlightedStat && !isPreviewDiffStat;

  return (
    <Tooltip
      color={statRangeInfo.color}
      content={
        <TooltipDerivedStatRange
          slug={statRangeSlug}
          statRange={statRange}
          baseRange={baseRange}
          upgradesRange={upgradesRange}
        />
      }
    >
      <StatRangeWrapper $isDim={isDim} $color={statRangeInfo.color}>
        <DerivedStatName>
          <StatIconWrapper>
            {statRangeInfo.images.map((image) => (
              <StatIcon key={image} image={image} />
            ))}
          </StatIconWrapper>
          {statRangeInfo.name}
        </DerivedStatName>
        <ValueWrapper>
          <StatValue $isBeforeValue={!!isPreviewDiffStat}>
            {statRangeInfo.formatter(statRange.min, statRange.max)}
          </StatValue>
          {!!isPreviewDiffStat && (
            <>
              <StatDiffIcon>
                {getComparisonIndicator([minDiffValue, maxDiffValue])}
              </StatDiffIcon>
              <StatValueDiff>
                {statRangeInfo.formatter(
                  statRangeInfo.rounder(statRange.min + minDiffValue),
                  statRangeInfo.rounder(statRange.max + maxDiffValue)
                )}
              </StatValueDiff>
            </>
          )}
        </ValueWrapper>
      </StatRangeWrapper>
    </Tooltip>
  );
}
